import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const FilterPanel = ({ onApplyFilters, onReset }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState({
    segment: 'all',
    dateRange: '7d',
    regions: [],
    comparisonMode: false
  });

  const segmentOptions = [
    { value: 'all', label: 'All Users' },
    { value: 'pro', label: 'Pro Tier' },
    { value: 'free', label: 'Free Tier' },
    { value: 'trial', label: 'Trial Users' },
    { value: 'churned', label: 'Churned Users' }
  ];

  const dateRangeOptions = [
    { value: '24h', label: 'Last 24 Hours' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const regionOptions = [
    { value: 'na', label: 'North America' },
    { value: 'eu', label: 'Europe' },
    { value: 'asia', label: 'Asia Pacific' },
    { value: 'latam', label: 'Latin America' },
    { value: 'mena', label: 'Middle East & Africa' }
  ];

  const handleRegionChange = (region) => {
    setFilters(prev => ({
      ...prev,
      regions: prev?.regions?.includes(region)
        ? prev?.regions?.filter(r => r !== region)
        : [...prev?.regions, region]
    }));
  };

  const handleApply = () => {
    onApplyFilters(filters);
    setIsExpanded(false);
  };

  const handleReset = () => {
    const defaultFilters = {
      segment: 'all',
      dateRange: '7d',
      regions: [],
      comparisonMode: false
    };
    setFilters(defaultFilters);
    onReset(defaultFilters);
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Icon name="Filter" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Filters</h3>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="lg:hidden p-2 rounded-lg hover:bg-muted/50 transition-smooth"
          aria-label="Toggle filters"
        >
          <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={20} />
        </button>
      </div>
      <div className={`space-y-4 ${isExpanded ? 'block' : 'hidden lg:block'}`}>
        <Select
          label="User Segment"
          options={segmentOptions}
          value={filters?.segment}
          onChange={(value) => setFilters(prev => ({ ...prev, segment: value }))}
        />

        <Select
          label="Date Range"
          options={dateRangeOptions}
          value={filters?.dateRange}
          onChange={(value) => setFilters(prev => ({ ...prev, dateRange: value }))}
        />

        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Geographic Regions
          </label>
          <div className="space-y-2">
            {regionOptions?.map(region => (
              <Checkbox
                key={region?.value}
                label={region?.label}
                checked={filters?.regions?.includes(region?.value)}
                onChange={() => handleRegionChange(region?.value)}
              />
            ))}
          </div>
        </div>

        <div className="pt-4 border-t border-border">
          <Checkbox
            label="Enable Comparison Mode"
            description="Compare with previous period"
            checked={filters?.comparisonMode}
            onChange={(e) => setFilters(prev => ({ ...prev, comparisonMode: e?.target?.checked }))}
          />
        </div>

        <div className="flex gap-2 pt-4">
          <Button
            variant="default"
            fullWidth
            onClick={handleApply}
            iconName="Check"
            iconPosition="left"
          >
            Apply Filters
          </Button>
          <Button
            variant="outline"
            onClick={handleReset}
            iconName="RotateCcw"
            iconPosition="left"
          >
            Reset
          </Button>
        </div>

        <div className="pt-4 border-t border-border">
          <button className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-muted/30 transition-smooth">
            <div className="flex items-center gap-2">
              <Icon name="Bookmark" size={16} className="text-primary" />
              <span className="text-sm font-medium text-foreground">Save Configuration</span>
            </div>
            <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;